import { ComponentPropsWithoutRef, useRef, useState } from 'react'
import { classNames } from '@/util/tailwindHelpers'

import Button, { IButtonProps } from './Button'
import ContextualMenu from './ContextualMenu'
import { ContextualMenuProps } from './ContextualMenu/types'

type CustomTrigger = (props: {
  onClick: () => void
  isMenuOpen: boolean
}) => React.ReactNode

interface Props extends ComponentPropsWithoutRef<'div'> {
  className?: string
  disabled?: boolean
  items: ContextualMenuProps['items']
  Trigger?: CustomTrigger | IButtonProps
  appearance?: ContextualMenuProps['appearance']
}

const DEFAULT_APPEARANCE: Props['appearance'] = {
  anchor: {
    x: 'left',
    y: 'bottom'
  }
}

export default function Dropdown({
  items,
  Trigger,
  disabled,
  className,
  appearance,
  ...rest
}: Props) {
  const buttonRef = useRef<HTMLDivElement>()
  const [isOpen, setIsOpen] = useState(false)

  const defaultTriggerProps = {
    disabled,
    onClick: () => setIsOpen(!isOpen)
  }
  const triggerProps = {
    ...defaultTriggerProps,
    isMenuOpen: isOpen
  }

  const appearanceConfig = { ...DEFAULT_APPEARANCE, ...appearance }

  return (
    <div className={classNames('relative', className)} {...rest}>
      <div ref={buttonRef}>
        {typeof Trigger === 'function' ? (
          Trigger(triggerProps)
        ) : (
          <DefaultTriggerComponent {...defaultTriggerProps} {...Trigger} />
        )}
      </div>

      <ContextualMenu
        className="my-1"
        buttonRef={buttonRef}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        items={items}
        appearance={appearanceConfig}
      />
    </div>
  )
}

interface DefaultTriggerComponentProps extends IButtonProps {}

const DefaultTriggerComponent = ({
  children = 'Trigger',
  className,
  ...rest
}: DefaultTriggerComponentProps) => {
  return (
    <Button
      theme="secondary"
      size="md"
      // disable the hover effect on the trigger button
      className={classNames('hover:!scale-100', className)}
      {...rest}
    >
      {children}
    </Button>
  )
}
